import { Card, Colors } from "@blueprintjs/core";
import { FormikProps, getIn } from "formik";
import {
  IDataTableColumn,
  IDefaultCriteriaValue,
  InlineButtonContainer,
  SearchQuery,
  useFGContext,
} from "nsitools-react";
import * as React from "react";
import { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";

import {
  AzureMemberApi,
  AzureMemberGridDto,
  AzureMemberSearchCriteria,
  SearchQueryOfAzureMemberSearchCriteria,
} from "../../../api";
import { useApiService, useCommonHooks } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { CancelButton, ValidateButton } from "../../applicationButtons";
import { DataTableBooleanColumn } from "../../customDataTable";
import { AdvancedSearchTable } from "../../searchTable/AdvancedSearchTable";

const CustomButtonContainer = styled(InlineButtonContainer)`
  display: flex;
  justify-content: space-between;
`;

export interface ICriteriaPanelDefaults {
  idcodePostal?: number;
  idmetier?: number;
}

export interface IUserSelectorProps {
  onRowClick?: (user: AzureMemberGridDto) => void;
  onClose?: () => void;
  allowMultipleSelection?: boolean;
  preSelectedIds?: number[];
  name: string;
  idField: string;
  formikInnerRef?: React.MutableRefObject<FormikProps<any>>;
  panelHeight?: string;
  showCriteriaPanel?: boolean;
  criteriaPanelDefaults?: ICriteriaPanelDefaults;
}

export const UserSelector: React.FunctionComponent<IUserSelectorProps> = ({
  name,
  idField,
  allowMultipleSelection,
  onClose = () => {},
  onRowClick = () => {},
  formikInnerRef,
  panelHeight,
  showCriteriaPanel,
  criteriaPanelDefaults,
}) => {
  const { t, tUnsafe } = useCommonHooks();
  const azureMemberApi = useApiService(AzureMemberApi);
  const [selectedIds, setSelectedIds] = React.useState([]);

  const getSearchCriterias = React.useCallback(
    () =>
      showCriteriaPanel ? azureMemberApi.getSearchCriterias(true) : undefined,
    [showCriteriaPanel, azureMemberApi]
  );

  const classicPanelDefaultCriterias = React.useMemo(() => {
    if (!showCriteriaPanel) return undefined;

    const result: IDefaultCriteriaValue[] = [];

    // IsActive = true par défaut (remplace l'ancienne checkbox "Actifs uniquement")
    result.push({
      criteria: "IsActive",
      searchMode: "Equals",
      value: true,
    });

    if (criteriaPanelDefaults) {
      if (criteriaPanelDefaults.idcodePostal) {
        result.push({
          criteria: "Localites",
          searchMode: "Equals",
          value: criteriaPanelDefaults.idcodePostal.toString(),
        });
      }

      if (criteriaPanelDefaults.idmetier) {
        result.push({
          criteria: "Metiers",
          searchMode: "Equals",
          value: criteriaPanelDefaults.idmetier.toString(),
        });
      }
    }

    return result;
  }, [showCriteriaPanel, criteriaPanelDefaults]);

  const ctx = useFGContext();
  const formik = React.useMemo(
    () => ctx?.formik ?? formikInnerRef?.current,
    [ctx?.formik, formikInnerRef]
  );

  React.useEffect(() => {
    const value = getIn(formik?.values ?? {}, name);
    if (selectedIds.length === 0 && value?.length > 0) {
      setSelectedIds(value);
    }
  }, [formik?.values, name, selectedIds.length]);

  const search = useCallback(
    (searchQuery: SearchQuery<AzureMemberSearchCriteria>) => {
      searchQuery.criteria = AzureMemberSearchCriteria.fromJS({
        ...searchQuery.criteria,
        // Le sélecteur pose un iduser dans formik : un membre Azure sans user est inutilisable.
        linkedUserOnly: true,
        // La vue azure ne filtre pas les comptes désactivés (fcb.vw_search_user le faisait) :
        // on garde le comportement "actifs uniquement", sauf quand le panneau de critères
        // expose la case IsActive et laisse l'utilisateur décider.
        activeOnly: !showCriteriaPanel,
      });
      return azureMemberApi.baseSearch(
        SearchQueryOfAzureMemberSearchCriteria.fromJS(searchQuery)
      );
    },
    [azureMemberApi, showCriteriaPanel]
  );

  const columns = useMemo<IDataTableColumn<AzureMemberGridDto>[]>(
    () => [
      {
        fieldName: "firstName",
        header: () => t(ETLCodes.FirstName),
      },
      {
        fieldName: "lastName",
        header: () => t(ETLCodes.LastName),
      },
      {
        fieldName: "fonction",
        header: () => t(ETLCodes.Fonction),
      },
      {
        fieldName: "isActive",
        header: () => t(ETLCodes.IsActive),
        alignment: "center",
        render: (item) => (
          <DataTableBooleanColumn
            checked={item.isActive}
            highlighted={selectedIds.includes(item.iduser)}
          />
        ),
      },
    ],
    [selectedIds, t]
  );

  const addId = React.useCallback(
    (id: number) => setSelectedIds((prev) => [...prev, id]),
    []
  );

  const removeId = React.useCallback((id: number) => {
    setSelectedIds((prev) => {
      let newValues = [...prev];
      newValues.splice(newValues.indexOf(id), 1);
      return newValues;
    });
  }, []);

  const handleItemClick = React.useCallback(
    (itemId: number) => {
      if (selectedIds?.indexOf(itemId) >= 0) {
        removeId(itemId);
      } else {
        addId(itemId);
      }
    },
    [addId, removeId, selectedIds]
  );

  const onRowClickFn = React.useCallback(
    (item: any) => {
      const itemId = getIn(item, idField);

      if (allowMultipleSelection) {
        handleItemClick(itemId);
        onRowClick(item);
      } else {
        formik.setFieldValue(name, itemId);
        onRowClick(item);
        onClose();
      }
    },
    [
      allowMultipleSelection,
      formik,
      handleItemClick,
      idField,
      name,
      onClose,
      onRowClick,
    ]
  );

  const selectedRowCss = React.useCallback(
    (item: any) => {
      if (
        allowMultipleSelection &&
        selectedIds?.indexOf(getIn(item, idField)) >= 0
      ) {
        return css`
          background: ${Colors.BLUE3} !important;
          & > td {
            color: ${Colors.WHITE} !important;
            font-weight: 500;
          }
        `;
      }
    },
    [allowMultipleSelection, idField, selectedIds]
  );

  const onValidate = React.useCallback(() => {
    formik.setFieldTouched(name);
    formik.setFieldValue(name, selectedIds, true);
    onClose();
  }, [formik, name, onClose, selectedIds]);

  return (
    <Card style={{ height: panelHeight }} className="user-selector-card">
      <AdvancedSearchTable
          sortKeys={{ lastName: "ASC", firstName: "ASC" }}
          searchFunction={search}
          getCriteriasFunction={showCriteriaPanel ? getSearchCriterias : undefined}
          columns={columns}
          onRowClick={onRowClickFn}
          customizeRowStyle={selectedRowCss}
          withCards={false}
          pageSize={10}
          availablePageSizes={[10]}
          storageMode="none"
          globalFilterStorageMode="state"
          classicPanelOptions={
            classicPanelDefaultCriterias
              ? { defaultCriterias: classicPanelDefaultCriterias }
              : undefined
          }
      />
      <CustomButtonContainer>
        <CancelButton onClick={onClose} minimal={false} />
        {allowMultipleSelection && (
          <ValidateButton onClick={onValidate} minimal={false} />
        )}
      </CustomButtonContainer>
    </Card>
  );
};

export default UserSelector;
